/*
  product-form.js (TMG theme, Dawn-derived)

  [2026-09-19 Sydney] Interaction polish (Astra brief CLAUDE-INTERACTION-POLISH-2026-09-18, Joel: "Direct on the live theme").
  Request states: idle -> pending -> confirmed | rejected | uncertain.
  - Kept: the aria-disabled double-submit guard, Shopify's own error text (and the cartError publish the
    gift-card recipient form listens to), returned-section rendering into the cart drawer, and the
    setActiveElement focus capture so the drawer hands focus back on close.
  - Fixed: a network/parse failure used to only console.error, leaving the shopper with nothing. The
    `finally` also cleared the cart's is-empty state even when the add failed; cart state now changes on
    confirmed success only.
  - Uncertain result (connection dropped, unreadable or 5xx reply): the add may still have reached Shopify,
    so the form never retries. It reads the real cart (/cart.js), redraws the drawer and count from the
    server, says what the cart now holds, and allows another add only after that check has succeeded.
  - The variant and quantity are read from the form at submit time; the shopper's selection is never
    reset, so a rejected or uncertain add leaves their choices in place.
  - The product page's sticky add bar (custom_liquid block `cl_sticky` in templates/product.json) forwards
    its click to this form's button, so the guard covers both. It gets the same busy state here.
  - One success response: the drawer (the store's existing confirmation) plus a single screen-reader status
    naming the exact item, variant and count from Shopify's reply. No toast, tick or extra animation.
  Rollback copy of the previous live file: C:\GIT\chief-of-staff\outputs\tmg-home-v4\interaction-fixes\rollback\product-form.js.live
*/
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

      this.hideErrors = this.dataset.hideErrors === 'true';
      this.requestState = 'idle';
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
      // pending: one add at a time. uncertain: the cart must be checked before any further add.
      if (this.requestState !== 'idle') return;

      this.handleErrorMessage(false, null, true);
      this.requestState = 'pending';
      this.setBusy(true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;
      const submittedVariantId = String(formData.get('id'));

      // outcome: 'confirmed' | 'rejected' | 'uncertain'
      let outcome = 'uncertain';
      let httpStatus = 0;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => {
          httpStatus = response.status;
          return response.text();
        })
        .then((text) => {
          let response = null;
          try {
            response = JSON.parse(text);
          } catch (e) {
            response = null;
          }

          if (response && response.status) {
            // Shopify answered with a reason (sold out, quantity limit, bad variant): a known rejection.
            outcome = 'rejected';
            publish(PUB_SUB_EVENTS.cartError, {source: 'product-form', productVariantId: formData.get('id'), errors: response.description, message: response.message});
            this.handleErrorMessage(response.description || response.message || this.strings().rejected);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          }

          if (!response) {
            // A 4xx without a readable body (e.g. rate limited) was refused before any change.
            // A 2xx or 5xx we can't read may or may not have added the item.
            if (httpStatus >= 400 && httpStatus < 500) {
              outcome = 'rejected';
              this.handleErrorMessage(this.strings().rejected, null, true);
            }
            return;
          }

          outcome = 'confirmed';
          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId: formData.get('id')});
          this.error = false;

          const quickAddModal = this.closest('quick-add-modal');
          try {
            if (quickAddModal) {
              document.body.addEventListener('modalClosed', () => {
                setTimeout(() => {
                  this.cart.renderContents(response);
                  this.announceAdded(response);
                });
              }, { once: true });
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
              this.announceAdded(response);
            }
          } catch (e) {
            // The add is confirmed; only drawing it failed. Redraw from the server rather than report a failure.
            console.error(e);
            this.readCart().then((state) => this.renderCartState(state)).catch(() => {});
          }
        })
        .catch((e) => {
          console.error(e);
          // A subscriber throwing after Shopify answered doesn't change what Shopify said.
          if (outcome === 'confirmed' || outcome === 'rejected') return;
          // Network failure or aborted request: the add may or may not have reached Shopify.
          outcome = 'uncertain';
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');

          if (outcome === 'uncertain') {
            this.reconcile(submittedVariantId);
            return;
          }

          this.requestState = 'idle';
          this.setBusy(false);
        });
    }

    /* ---------- uncertain outcome: read the real cart before allowing another add ---------- */

    reconcile(variantId) {
      this.requestState = 'uncertain';
      this.setBusy(true);
      this.pendingVariantId = variantId || this.pendingVariantId;
      this.handleErrorMessage(this.strings().checking, null, true);

      this.readCart()
        .then((state) => {
          this.renderCartState(state);
          const matching = (state.cart.items || []).filter((item) => String(item.variant_id) === String(this.pendingVariantId));
          const count = matching.reduce((sum, item) => sum + item.quantity, 0);

          this.requestState = 'idle';
          this.setBusy(false);

          if (count > 0) {
            publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId: this.pendingVariantId});
            this.handleErrorMessage(this.strings().unconfirmedHas(count), this.cart && this.cart.open
              ? { label: this.strings().viewCart, run: (button) => this.cart.open(button) }
              : { label: this.strings().viewCart, href: window.routes.cart_url }, true);
          } else {
            this.handleErrorMessage(this.strings().unconfirmedNone, null, true);
          }
        })
        .catch((e) => {
          console.error(e);
          // Still can't reach Shopify: keep further adds blocked (not busy, just locked) and offer the check again.
          this.setBusy(false, true);
          this.handleErrorMessage(this.strings().offline, { label: this.strings().checkCart, run: () => this.reconcile() }, true);
        });
    }

    readCart() {
      const cartUrl = `${window.routes.cart_url}.js`;
      const sectionIds = this.cart && this.cart.getSectionsToRender
        ? this.cart.getSectionsToRender().map((section) => section.id)
        : [];

      const cartRequest = fetch(cartUrl, { cache: 'no-store', headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(`cart.js ${response.status}`);
          return response.json();
        });
      const sectionsRequest = sectionIds.length
        ? fetch(`${window.routes.cart_url}?sections=${sectionIds.join(',')}`, { cache: 'no-store' })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
        : Promise.resolve(null);

      return Promise.all([cartRequest, sectionsRequest]).then(([cart, sections]) => ({ cart, sections }));
    }

    renderCartState(state) {
      if (!this.cart) return;
      this.cart.classList.toggle('is-empty', state.cart.item_count === 0);
      if (!state.sections || !this.cart.getSectionsToRender) return;
      const inner = this.cart.querySelector('.drawer__inner');
      if (inner) inner.classList.toggle('is-empty', state.cart.item_count === 0);
      this.cart.getSectionsToRender().forEach((section) => {
        const html = state.sections[section.id];
        const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (!html || !target) return;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section');
        if (source) target.innerHTML = source.innerHTML;
      });
      // The redraw replaced the overlay; rebind its close click as renderContents does.
      this.cart.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.cart.close.bind(this.cart));
    }

    /* ---------- feedback ---------- */

    announceAdded(item) {
      if (!item || !item.product_title) return;
      const variant = item.variant_title && !/^default title$/i.test(item.variant_title) ? `, ${item.variant_title}` : '';
      // The store calls its cart the Satchel (drawer heading, header link).
      const text = `Added ${item.product_title}${variant} to your satchel. ${item.quantity} in your satchel.`;
      // One status line. Inside the drawer dialog when there is one (focus moves there; the dialog is
      // aria-modal), otherwise beside the form. Our own node: the drawer's LiveRegionText sits inside
      // cart-drawer-items, which the cartUpdate refetch replaces a moment later.
      const dialog = this.cart && this.cart.querySelector('.drawer__inner');
      const target = this.statusRegion(dialog || this);
      target.textContent = '';
      setTimeout(() => { target.textContent = text; }, 150);
    }

    statusRegion(host) {
      let el = host.querySelector(':scope > .v5p-add-status');
      if (!el) {
        el = document.createElement('p');
        el.className = 'visually-hidden v5p-add-status';
        el.setAttribute('role', 'status');
        host.appendChild(el);
      }
      return el;
    }

    setBusy(busy, locked = false) {
      const buttons = [this.submitButton, this.stickyButton()].filter(Boolean);
      buttons.forEach((button) => {
        if (busy || locked) {
          button.setAttribute('aria-disabled', true);
          if (busy) button.setAttribute('aria-busy', 'true');
          else button.removeAttribute('aria-busy');
        } else {
          button.removeAttribute('aria-busy');
          // A sold-out rejection on a quick-add card keeps its own lock (this.error).
          if (button !== this.submitButton || !this.error) button.removeAttribute('aria-disabled');
        }
      });
    }

    stickyButton() {
      const section = this.closest('[id^="MainProduct-"]');
      if (!section) return null;
      return document.getElementById(`croStickyAdd-${section.id.replace('MainProduct-', '')}`);
    }

    strings() {
      return {
        rejected: "We couldn't add this item. Your choices are kept. Please try again.",
        checking: "We couldn't confirm that. Checking your cart...",
        unconfirmedHas: (count) => `We couldn't confirm that add. Your cart now has ${count} of this item, so check it before adding more.`,
        unconfirmedNone: "That wasn't added. Your cart doesn't have this item and your choices are kept. You can try again.",
        offline: "We couldn't confirm whether this was added. Check your connection, then check your cart before adding again.",
        checkCart: 'Check cart',
        viewCart: 'View cart',
      };
    }

    handleErrorMessage(errorMessage = false, action = null, always = false) {
      // hideErrors hands Shopify's rejection text to the gift-card recipient form (via cartError).
      // Connection states are ours alone, so those calls pass always = true.
      if (this.hideErrors && !always) return;

      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      // Quick-add cards keep their own sold-out behaviour for Shopify rejections; they only get a
      // message area when a connection state needs explaining.
      if (!this.errorMessageWrapper && always && errorMessage) this.errorMessageWrapper = this.createErrorWrapper();
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      const hadFocus = this.actionButton && this.actionButton === document.activeElement;
      if (this.actionButton) {
        this.actionButton.remove();
        this.actionButton = null;
      }

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
        if (action) {
          const el = document.createElement(action.href ? 'a' : 'button');
          el.className = 'link underlined-link v5p-form-action';
          el.textContent = action.label;
          // Inline in the sentence (the wrapper is a flex row, so a sibling would wrap into a column).
          el.style.fontSize = 'inherit';
          el.style.marginLeft = '0.4rem';
          el.style.padding = '0';
          el.style.whiteSpace = 'nowrap';
          if (action.href) {
            el.href = action.href;
          } else {
            el.type = 'button';
            el.addEventListener('click', () => action.run(el));
          }
          this.errorMessage.append(' ', el);
          this.actionButton = el;
        }
      }

      // Never leave focus on a removed control: move it to the replacement action, else the add button.
      if (hadFocus) (this.actionButton || this.submitButton).focus();
    }

    createErrorWrapper() {
      // Quick-add cards have no message area; give them the same one the product page uses.
      const wrapper = document.createElement('div');
      wrapper.className = 'product-form__error-message-wrapper';
      wrapper.setAttribute('role', 'alert');
      wrapper.hidden = true;
      wrapper.style.fontSize = '1.2rem';
      const message = document.createElement('span');
      message.className = 'product-form__error-message';
      wrapper.appendChild(message);
      this.prepend(wrapper);
      return wrapper;
    }
  });
}
